import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { ThemeColors } from '../models/theme-colors';
import { ThemePalette } from '../models/theme-palette';

@Injectable({
  providedIn: 'root'
})
export class StyleService {
  private tenantColors: ThemeColors = {};
  private currentPageOverride: ThemeColors | null = null;
  private activeGraphColors: string[] = [];
  private activeBackground: string = '';

  private static readonly BOOTSTRAP_CSS_CLASSES: string[] = [
    '--bs-primary',
    '--bs-primary-rgb',
    '--bs-secondary',
    '--bs-secondary-rgb',
    '--bs-body-bg',
    '--bs-success',
    '--bs-success-rgb',
    '--bs-warning',
    '--bs-warning-rgb',
    '--bs-danger',
    '--bs-danger-rgb',
    '--bs-info',
    '--bs-info-rgb',
  ];

  private static readonly DEFAULT_GRAPH_COLORS = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
    '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
  ];

  private static readonly GRAPH_COLOR_THRESHOLD = 40;

  constructor(
    @Inject(DOCUMENT) private document: Document
  ) { }


  applyTenantTheme(palette: ThemePalette, overrides?: ThemeColors): void {
    this.tenantColors = {
      primary: overrides?.primary || palette.primary,
      secondary: overrides?.secondary || palette.secondary,
      background: overrides?.background || palette.background,
      success: overrides?.success || palette.success,
      warning: overrides?.warning || palette.warning,
      danger: overrides?.danger || palette.danger,
      info: overrides?.info || palette.info,
      graphColors: overrides?.graphColors || palette.graphColors,
    };
    this.applyColors(this.tenantColors);
  }

  applyResolvedTheme(colors: ThemeColors): void {
    this.tenantColors = { ...colors };
    this.applyColors(this.tenantColors);
    if (this.currentPageOverride) {
      this.applyPageOverride(this.currentPageOverride);
    }
  }

  applyPageOverride(overrides: ThemeColors): void {
    this.currentPageOverride = overrides;
    const merged: ThemeColors = { ...this.tenantColors };
    if (overrides.primary) merged.primary = overrides.primary;
    if (overrides.secondary) merged.secondary = overrides.secondary;
    if (overrides.background) merged.background = overrides.background;
    if (overrides.success) merged.success = overrides.success;
    if (overrides.warning) merged.warning = overrides.warning;
    if (overrides.danger) merged.danger = overrides.danger;
    if (overrides.info) merged.info = overrides.info;
    if (overrides.graphColors) merged.graphColors = overrides.graphColors;
    this.applyColors(merged);
  }

  revertToTenantTheme(): void {
    this.currentPageOverride = null;
    const root = this.getRoot();
    StyleService.BOOTSTRAP_CSS_CLASSES.forEach(p => root.style.removeProperty(p));
    this.activeGraphColors = this.tenantColors.graphColors ?? [];
    this.applyColors(this.tenantColors);
  }

  clearTheme(): void {
    this.tenantColors = {};
    this.currentPageOverride = null;
    this.activeGraphColors = [];
    this.activeBackground = '';
    const root = this.getRoot();
    StyleService.BOOTSTRAP_CSS_CLASSES.forEach(p => root.style.removeProperty(p));
  }

  getGraphColors(): string[] {
    const bg = this.activeBackground;
    const isTooClose = (color: string) =>
      bg ? this.hexDistance(color, bg) < StyleService.GRAPH_COLOR_THRESHOLD : false;

    const colors = this.activeGraphColors.filter(c => !isTooClose(c));
    if (colors.length >= 4) {
      return colors;
    }

    const defaults = StyleService.DEFAULT_GRAPH_COLORS;
    const filled = [...colors];
    // Start at the original active count to keep consistent color assignment
    let i = this.activeGraphColors.length;
    const limit = i + defaults.length;
    while (filled.length < 4 && i < limit) {
      const candidate = defaults[i % defaults.length];
      if (!isTooClose(candidate)) {
        filled.push(candidate);
      }
      i++;
    }
    return filled;
  }

  setStyle(scheme: string): void {
    const root = this.getRoot();
    root.setAttribute('data-scheme', scheme);
    this.clearTheme();
  }

  private applyColors(colors: ThemeColors): void {
    const root = this.getRoot();

    this.applyColorWithRgb(root, '--bs-primary', '--bs-primary-rgb', colors.primary);
    this.applyColorWithRgb(root, '--bs-secondary', '--bs-secondary-rgb', colors.secondary);
    this.applyColorWithRgb(root, '--bs-success', '--bs-success-rgb', colors.success);
    this.applyColorWithRgb(root, '--bs-warning', '--bs-warning-rgb', colors.warning);
    this.applyColorWithRgb(root, '--bs-danger', '--bs-danger-rgb', colors.danger);
    this.applyColorWithRgb(root, '--bs-info', '--bs-info-rgb', colors.info);

    if (colors.background) {
      root.style.setProperty('--bs-body-bg', colors.background);
    } else {
      root.style.removeProperty('--bs-body-bg');
    }

    this.activeGraphColors = colors.graphColors ?? this.activeGraphColors;
    this.activeBackground = colors.background ?? '';
  }

  private applyColorWithRgb(root: HTMLElement, colorProperty: string, rgbProperty: string, color?: string): void {
    if (!color) {
      root.style.removeProperty(colorProperty);
      root.style.removeProperty(rgbProperty);
      return;
    }

    root.style.setProperty(colorProperty, color);
    const rgb = this.hexToRgb(color);
    if (rgb) {
      root.style.setProperty(rgbProperty, rgb);
    } else {
      root.style.removeProperty(rgbProperty);
    }
  }

  private getRoot(): HTMLElement {
    return (this.document.getElementById('root') as HTMLElement) ?? this.document.documentElement;
  }

  private hexToRgb(hex: string): string | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : null;
  }

  private hexDistance(a: string, b: string): number {
    const parse = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : null;
    };
    const ca = parse(a);
    const cb = parse(b);
    if (!ca || !cb) return Infinity;
    return Math.sqrt((ca[0] - cb[0]) ** 2 + (ca[1] - cb[1]) ** 2 + (ca[2] - cb[2]) ** 2);
  }
}
